import React, { useState, useCallback } from "react";
import { toast } from "react-toastify";

import useDepartments from "./useDepartments";
import DepartmentDialog from "./DepartmentDialog";
import DeleteDepartmentDialog from "./DeleteDepartmentDialog";
import MembersView from "./MembersView";
import {
  createDepartment,
  updateDepartment,
  deleteDepartment,
} from "./departmentsApi";
import { findDepartmentIndex } from "./departmentState";
import { normalizeApiError, formatErrorMessage } from "../../utils/apiErrors";
import { CACHE_TTL_NOTICE } from "./AccessManagerPanel";
import "./AccessManagerPanel.css";

/**
 * DepartmentsTab — table of departments with inline expandable members,
 * create/edit dialog, and delete confirmation dialog.
 *
 * Owns its data through `useDepartments()` and orchestrates the optimistic
 * mutation paths for create, update, delete, and member count adjustments.
 *
 * Requirements: 2.3–2.10, 3.5–3.7, 4.2–4.5, 5.3–5.5, 6.8–6.12, 11.1, 11.2, 12.3–12.5, 12.7
 */
export default function DepartmentsTab() {
  const {
    departments,
    loading,
    error,
    retry,
    insertDepartment,
    insertDepartmentAt,
    replaceDepartment,
    removeDepartment,
    adjustMemberCount,
  } = useDepartments();

  const [dialogMode, setDialogMode] = useState(null); // "create" | "edit" | null
  const [editingDepartment, setEditingDepartment] = useState(null);
  const [deletingDepartment, setDeletingDepartment] = useState(null);
  const [expandedId, setExpandedId] = useState(null); // one MembersView open at a time

  // -- Dialog handlers -------------------------------------------------------

  const handleOpenCreate = useCallback(() => {
    setDialogMode("create");
    setEditingDepartment(null);
  }, []);

  const handleOpenEdit = useCallback((department) => {
    setDialogMode("edit");
    setEditingDepartment(department);
  }, []);

  const handleCloseDialog = useCallback(() => {
    setDialogMode(null);
    setEditingDepartment(null);
  }, []);

  const handleOpenDelete = useCallback((department) => {
    setDeletingDepartment(department);
  }, []);

  const handleCloseDelete = useCallback(() => {
    setDeletingDepartment(null);
  }, []);

  // -- Mutation orchestration -------------------------------------------------

  /**
   * Create: no optimistic step (id unknown). On success insert the returned
   * department and toast; on failure the dialog shows its own error (Req 3.5–3.7).
   */
  const handleCreateSubmit = useCallback(
    async (payload) => {
      const response = await createDepartment(payload);
      const created = response?.data?.department ?? response?.data ?? response?.data;
      insertDepartment(created);
      handleCloseDialog();
      toast.success(`Department created. ${CACHE_TTL_NOTICE}`);
    },
    [insertDepartment, handleCloseDialog]
  );

  /**
   * Update: optimistically replace with pending values. On success toast;
   * on failure restore the pre-submit snapshot and re-throw so the dialog
   * shows its own error (Req 4.2–4.5).
   */
  const handleEditSubmit = useCallback(
    async (payload) => {
      const department = editingDepartment;
      if (!department) return;

      // Snapshot before mutation
      const snapshot = {
        name: department.name,
        description: department.description,
        scopes: department.scopes,
      };

      // Optimistic update
      replaceDepartment(department.id, payload);

      try {
        await updateDepartment(department.id, payload);
        handleCloseDialog();
        toast.success(`Department updated. ${CACHE_TTL_NOTICE}`);
      } catch (err) {
        // Rollback to pre-submit snapshot
        replaceDepartment(department.id, snapshot);
        throw err; // re-throw so DepartmentDialog shows the error
      }
    },
    [editingDepartment, replaceDepartment, handleCloseDialog]
  );

  /**
   * Delete: optimistically remove after recording the index. On success toast;
   * on failure re-insert at the original index (Req 5.3–5.5).
   */
  const handleDeleteConfirm = useCallback(async () => {
    const department = deletingDepartment;
    if (!department) return;

    const index = findDepartmentIndex(departments, department.id);
    const snapshot = department;

    // Optimistic remove
    removeDepartment(department.id);
    setDeletingDepartment(null);

    try {
      await deleteDepartment(department.id);
      toast.success(`Department deleted. ${CACHE_TTL_NOTICE}`);
    } catch (err) {
      // Rollback: re-insert at original index
      insertDepartmentAt(snapshot, index === -1 ? 0 : index);
      const message = formatErrorMessage(normalizeApiError(err));
      toast.error(message);
    }
  }, [deletingDepartment, departments, removeDepartment, insertDepartmentAt]);

  // -- Member count delta paths (Req 6.8, 6.9, 6.11, 6.12) -------------------

  const handleMemberCountDelta = useCallback(
    (departmentId, delta) => {
      adjustMemberCount(departmentId, delta);
    },
    [adjustMemberCount]
  );

  // -- Expand toggle -----------------------------------------------------------

  const handleToggleExpand = useCallback((departmentId) => {
    setExpandedId((prev) => (prev === departmentId ? null : departmentId));
  }, []);

  // -- Render ------------------------------------------------------------------

  // Loading state: skeleton rows (Req 2.7)
  if (loading) {
    return (
      <div className="departments-tab">
        <div className="departments-tab__notice">
          Department changes take effect within 60 seconds due to cache TTL.
        </div>
        <table className="access-manager-panel__table" role="table">
          <thead>
            <tr>
              <th>Department</th>
              <th>Scopes</th>
              <th>Members</th>
              <th>Created by</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {[1, 2, 3].map((i) => (
              <tr key={i} className="departments-tab__skeleton-row">
                <td><div className="departments-tab__skeleton-cell" /></td>
                <td><div className="departments-tab__skeleton-cell" /></td>
                <td><div className="departments-tab__skeleton-cell" /></td>
                <td><div className="departments-tab__skeleton-cell" /></td>
                <td><div className="departments-tab__skeleton-cell" /></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );
  }

  // Error state (Req 2.8)
  if (error) {
    return (
      <div className="departments-tab">
        <div className="departments-tab__notice">
          Department changes take effect within 60 seconds due to cache TTL.
        </div>
        <div className="access-manager-panel__error" role="alert">
          {error}
          <button type="button" onClick={retry}>
            Retry
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="departments-tab">
      {/* Persistent cache-TTL notice (Req 11.2) */}
      <div className="departments-tab__notice">
        Department changes take effect within 60 seconds due to cache TTL.
      </div>

      {/* Create Department control (Req 2.10) */}
      <button
        type="button"
        className="departments-tab__create-btn"
        onClick={handleOpenCreate}
      >
        Create Department
      </button>

      {/* Empty state (Req 2.9) */}
      {departments.length === 0 && (
        <div className="departments-tab__empty">
          No departments yet. Create one to grant scopes to a group of users.
        </div>
      )}

      {/* Departments table (Req 2.3) */}
      {departments.length > 0 && (
        <table className="access-manager-panel__table" role="table">
          <thead>
            <tr>
              <th>Department</th>
              <th>Scopes</th>
              <th>Members</th>
              <th>Created by</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {departments.map((dept) => (
              <React.Fragment key={dept.id}>
                <tr>
                  {/* Department column: name + description */}
                  <td>
                    <span className="departments-tab__name">{dept.name}</span>
                    {dept.description && (
                      <span className="departments-tab__description">
                        {dept.description}
                      </span>
                    )}
                  </td>

                  {/* Scopes column: one chip per scope (Req 2.4) */}
                  <td className="departments-tab__scopes">
                    {(dept.scopes || []).map((scope) => (
                      <span key={scope} className="departments-tab__scope-chip">
                        {scope}
                      </span>
                    ))}
                  </td>

                  {/* Members column: expand toggle (Req 2.5) */}
                  <td>
                    <button
                      type="button"
                      className="departments-tab__members-toggle"
                      aria-expanded={expandedId === dept.id}
                      aria-controls={`members-panel-${dept.id}`}
                      onClick={() => handleToggleExpand(dept.id)}
                    >
                      {dept.member_count}{" "}
                      {dept.member_count === 1 ? "member" : "members"}
                    </button>
                  </td>

                  {/* Created by column (Req 2.6) */}
                  <td>{dept.created_by || "—"}</td>

                  {/* Actions column */}
                  <td>
                    <button
                      type="button"
                      className="departments-tab__edit-btn"
                      onClick={() => handleOpenEdit(dept)}
                      aria-label={`Edit ${dept.name}`}
                    >
                      Edit
                    </button>
                    <button
                      type="button"
                      className="departments-tab__delete-btn"
                      onClick={() => handleOpenDelete(dept)}
                      aria-label={`Delete ${dept.name}`}
                    >
                      Delete
                    </button>
                  </td>
                </tr>

                {/* Expandable members row */}
                {expandedId === dept.id && (
                  <MembersView
                    department={dept}
                    onMemberCountDelta={(delta) =>
                      handleMemberCountDelta(dept.id, delta)
                    }
                    onRollbackCountDelta={(delta) =>
                      handleMemberCountDelta(dept.id, delta)
                    }
                  />
                )}
              </React.Fragment>
            ))}
          </tbody>
        </table>
      )}

      {/* DepartmentDialog — create or edit */}
      <DepartmentDialog
        open={dialogMode === "create" || dialogMode === "edit"}
        mode={dialogMode || "create"}
        department={editingDepartment}
        onClose={handleCloseDialog}
        onSubmit={dialogMode === "edit" ? handleEditSubmit : handleCreateSubmit}
      />

      {/* DeleteDepartmentDialog */}
      <DeleteDepartmentDialog
        open={deletingDepartment !== null}
        department={deletingDepartment}
        onClose={handleCloseDelete}
        onConfirm={handleDeleteConfirm}
      />
    </div>
  );
}
